import { useEffect, useRef } from 'react';
import { trackExitIntent } from '@/utils/analytics';

/**
 * Tracks exit intent signals:
 * - Mouse leaving viewport (desktop — moving cursor toward browser close/tab bar)
 * - Tab switching (visibility change)
 * - Browser back button
 */
export const useExitIntent = (pageName: string) => {
  const firedMouse = useRef(false);

  useEffect(() => {
    firedMouse.current = false;

    // 1. Mouse leaves viewport toward top (exit intent)
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !firedMouse.current) {
        firedMouse.current = true;
        trackExitIntent('mouse_leave', pageName);
      }
    };

    // 2. Tab switch / minimize
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackExitIntent('tab_switch', pageName);
      }
    };

    // 3. Back button (popstate)
    const handlePopState = () => {
      trackExitIntent('back_button', pageName);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [pageName]);
};
